// Push inside the native shell (X13-1009c). The Capacitor shell loads this web app, so the web boot wires the plugin when, and only
// when, the page runs in the native shell on iOS or Android. In a browser nothing here runs and the plugin module is never
// loaded. The token is posted to POST /api/v1/push/native (X13-1009a). A signed-out start gets 401 and stores nothing; the plugin
// registers again on every start, so the next start after sign-in sends the token. A tapped notification opens its portal link
// inside the app.
import { wirePush, type Platform } from "../nativePush";

type NativeGlobal = { isNativePlatform?: () => boolean; getPlatform?: () => string };

/** The native shell's global, or null in a browser. */
function nativeShell(): NativeGlobal | null {
  if (typeof window === "undefined") return null;
  const cap = (window as { Capacitor?: NativeGlobal }).Capacitor;
  return cap?.isNativePlatform?.() ? cap : null;
}

/** Posts one token to the native push route. Not through `call`: a 401 must not send the shell to sign-in, it just stores nothing. */
async function postNativeToken(path: "/push/native", body: { platform: Platform; token: string }): Promise<void> {
  await fetch(`/api/v1${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  }).catch(() => undefined);
}

/**
 * Wires push when the page runs in the native shell on iOS or Android; resolves to false and does nothing anywhere else.
 * `open` moves the shell to a portal path (the default navigates the shell itself). Resolves to whether permission was granted.
 */
export async function bootNativePush(open: (path: string) => void = (path) => window.location.assign(path)): Promise<boolean> {
  const cap = nativeShell();
  const platform = cap?.getPlatform?.();
  if (platform !== "ios" && platform !== "android") return false;
  // loaded only here, so a browser never fetches the plugin
  const { PushNotifications } = await import("@capacitor/push-notifications");
  return wirePush(PushNotifications, platform, window.location.origin, postNativeToken, open);
}
